import { SecurityUpdateOutlined } from "@mui/icons-material";
import { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AlertVariant } from "~/src/components/Alerts/AlertDefault";
import BottomLine from "~/src/components/BottomLine";
import ButtonDefault, { ButtonVariant } from "~/src/components/Button/ButtonDefault";
import { RadioButton } from "~/src/components/Form/Radio";
import { retrieve, store } from "~/src/contexts/StorageContext/helpers";
import UIContext from "~/src/contexts/UIContext/UIProvider";
import getAppInfo, { AppInfo } from "~/src/helpers/getAppInfo";
import { getEnvironmentLabel } from "~/src/appConfig";
import { useDialog } from "~/src/hooks/useDialog";
import { useReload } from "~/src/hooks/useReload";

type VersionCheck = {
  appInfo: AppInfo;
  notifyIfNoUpdate: boolean;
};

const Innstillinger = (): JSX.Element => {
  const ui = useContext(UIContext);
  const appInfo = getAppInfo();
  const { t, i18n } = useTranslation();
  const reload = useReload("Innstillinger");
  const dialog = useDialog({
    title: t("pages.innstillinger.sprak.dialogTittel"),
  });

  useEffect(() => {
    const storedVersionCheck = retrieve<VersionCheck>("versionCheck");
    if (
      storedVersionCheck === undefined ||
      storedVersionCheck.appInfo.version !== appInfo.version
    ) {
      ui.showSnackbar({ message: t("app.updated"), severity: AlertVariant.Info });
    } else if (storedVersionCheck.notifyIfNoUpdate) {
      ui.showSnackbar({ message: t("app.noUpdate"), severity: AlertVariant.Info });
    }
    store("versionCheck", { appInfo: appInfo, notifyIfNoUpdate: false });
  }, []);

  const changeLanguageHandler = (lng: string) => {
    i18n.changeLanguage(lng);
    const language =
      lng === "nb" ? t("pages.innstillinger.sprak.bokmal") : t("pages.innstillinger.sprak.nynorsk");
    dialog.show({
      message: t("pages.innstillinger.sprak.dialogTekst", { language }),
    });
  };

  const checkForNewVersionHandler = () => {
    store("versionCheck", { appInfo: appInfo, notifyIfNoUpdate: true });
    reload.reload();
  };

  return (
    <div className="pt-6 ">
      <div className="mx-4">
        <dl>
          <dt>{t("pages.innstillinger.appVersjon")}</dt>
          <BottomLine />
          <dd className="flex justify-between py-4 items-center">
            <span>{t("pages.innstillinger.installert")}</span>
            <span className="text-lg">
              {t("pages.innstillinger.versjon")}
              {" " + appInfo.version}
              {import.meta.env.MODE !== "prod" && (
                <span className="text-ldir-red-900"> ({getEnvironmentLabel()})</span>
              )}
            </span>
          </dd>
        </dl>
        <BottomLine />
        <ButtonDefault
          className="my-6"
          id="oppdater"
          onClick={() => checkForNewVersionHandler()}
          label={t("pages.innstillinger.update.get")}
          variant={ButtonVariant.Primary}
          icon={<SecurityUpdateOutlined />}
        />
        <BottomLine />
      </div>

      <fieldset className="my-6 font-bold">
        <legend className="mx-4 font-normal">{t("pages.innstillinger.byttSpråk")}</legend>
        <RadioButton
          checked={i18n.language != "nn"} // to account for 'nb' and any null/falsy values
          onChange={() => changeLanguageHandler("nb")}
          label={t("pages.innstillinger.sprak.bokmal")}
          bgOverflow
        />
        <RadioButton
          checked={i18n.language === "nn"}
          onChange={() => changeLanguageHandler("nn")}
          label={t("pages.innstillinger.sprak.nynorsk")}
          bgOverflow
        />
        <div className="mx-4">
          <BottomLine />
        </div>
      </fieldset>
      {dialog.element}
    </div>
  );
};

export default Innstillinger;
